//imports
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Link,
  Breadcrumbs,
  Stack,
  Button,
  Grid,
  AppBar,
  Toolbar,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  MenuItem,
  Checkbox,
  InputLabel,
  Select,
  Tooltip,
  Divider,
  Chip,
  Paper,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UndoIcon from "@mui/icons-material/Undo";
import PrintIcon from "@mui/icons-material/Print";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Navbar from "../../components/common/Navbar";
import { useStyles } from "../../assets/styles/Theme";
import {
  externalgetApi,
  externalpostApi,
} from "../../services/external/externalApis";
import { ORDER_API_04, ORDER_API_19 } from "../../api/ApiModel";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  CON_ORDER_EXCHANGE,
  CON_ORDER_REFUND,
  CON_ORDER_RETURN,
  CON_ORDER_VOID,
  CON_ORTC_EXCHANGE,
  CON_ORTC_REFUND,
  CON_ORTC_RETURN,
  CON_ORTC_VOID,
  CON_STATUS_ACTIVE,
  CON_STATUS_NO,
  CON_STATUS_PENDING,
  CON_STATUS_RETURN,
  CON_STATUS_YES,
  CON_STATUS_DELETED,
  CON_NOTE_EXPIRY_DAYS,
  generateReturnNo,
  getReturnActivityLabelFromLine,
} from "../../utils/sys/Constants";
import {
  getNoteRefundStatus,
  getUnredeemedCeNotes,
} from "../../utils/order/noteRefundStatus";
import currencyFormat from "../../utils/common/CurrencyFormat";
import { validateCashFields } from "../../utils/common/Validations";

const PENALTY_RATE_MAX = 100;

// Shared no-spinner sx for number inputs
const noSpinnerSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
};

function ItemActivityHistory({ history, expanded, onToggle, theme }) {
  const gridColumns = {
    xs: "minmax(0, 1.4fr) 48px 80px 88px",
    md: "minmax(0, 1.6fr) 56px 88px 96px",
  };

  const headerCellSx = {
    fontWeight: 700,
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  };

  return (
    <>
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 0.85,
          borderTop: `1px solid ${theme.palette.stroke.main}`,
          background: theme.palette.background.default,
          cursor: "pointer",
          userSelect: "none",
          transition: "background-color 0.15s ease",
          "&:hover": {
            background: `${theme.palette.primary.main}08`,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <HistoryOutlinedIcon
            sx={{ fontSize: 16, color: theme.palette.text.secondary, flexShrink: 0 }}
          />
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}
          >
            Previous activity
          </Typography>
          <Typography
            component="span"
            variant="caption"
            sx={{
              color: theme.palette.text.disabled,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.stroke.main}`,
              borderRadius: 10,
              px: 0.75,
              py: 0.1,
              fontSize: "0.65rem",
              fontWeight: 700,
              lineHeight: 1.6,
            }}
          >
            {history.length}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-label={expanded ? "Collapse activity history" : "Expand activity history"}
          sx={{ color: theme.palette.text.secondary }}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: 1.5, pt: 0.5, background: theme.palette.background.default }}>
          <Box
            sx={{
              border: `1px solid ${theme.palette.stroke.main}`,
              borderRadius: 1.5,
              overflow: "hidden",
              background: theme.palette.background.paper,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: gridColumns,
                gap: 1,
                px: 1.5,
                py: 0.85,
                borderBottom: `1px solid ${theme.palette.stroke.main}`,
                background: theme.palette.background.tertiary,
              }}
            >
              <Typography sx={headerCellSx}>Transaction Type</Typography>
              <Typography sx={{ ...headerCellSx, textAlign: "center" }}>Qty</Typography>
              <Typography sx={{ ...headerCellSx, textAlign: "right" }}>Rate</Typography>
              <Typography sx={{ ...headerCellSx, textAlign: "right" }}>Amount</Typography>
            </Box>
            {history.map((line, idx) => {
              const typeLabel = getReturnActivityLabelFromLine(line);

              return (
                <Box
                  key={idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: gridColumns,
                    gap: 1,
                    px: 1.5,
                    py: 0.85,
                    alignItems: "center",
                    borderBottom:
                      idx < history.length - 1
                        ? `1px solid ${theme.palette.stroke.main}`
                        : "none",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      background: `${theme.palette.primary.main}06`,
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={typeLabel}
                  >
                    {typeLabel}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.8rem", textAlign: "center", color: theme.palette.text.primary }}
                  >
                    {line.QTY ?? "—"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.8rem", textAlign: "right", color: theme.palette.text.secondary }}
                  >
                    {currencyFormat(line.RATE)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.8rem",
                      textAlign: "right",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {currencyFormat(line.AMOUNT)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </>
  );
}

function OrderReturn() {
  // Hooks start
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const { ORDERCODE, actionText, selectedItem } = location.state || {};
  const INVNO = selectedItem?.INVNO;
  const actionLabel = actionText || "Return";

  const [orderData, setOrderData] = useState([]);
  const [remarkOptions, setRemarkOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState({});
  const [checkedRows, setCheckedRows] = useState({});
  const [additionalData, setAdditionalData] = useState(null);
  const [returnQty, setReturnQty] = useState({});
  const [returnRate, setReturnRate] = useState({});
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [noteData, setNoteData] = useState(null);
  const [openNoteModal, setOpenNoteModal] = useState(false);
  const printRef = useRef(null);
  // penalty state: { [idno]: { type: "rate"|"amount", value: "" } }
  const [penalty, setPenalty] = useState({});
  const [expandedHistory, setExpandedHistory] = useState({});
  const [orderReturnHeaders, setOrderReturnHeaders] = useState([]);
  const [openUnredeemedDialog, setOpenUnredeemedDialog] = useState(false);
  const [cancelConfirmNote, setCancelConfirmNote] = useState(null);
  const [cancellingReturnNo, setCancellingReturnNo] = useState(null);

  const SYS_USER = useSelector((state) => state.user) || {};
  const { USERNAME, COMPCODE, LOCCODE, RESTCODE, USERREFCODE, COMPNAME, RESTNAME, ADDRESS } =
    SYS_USER.USER_PARAM || {};

  const actionLower = actionText?.toLowerCase() ?? "";
  const isRefund = actionLower === CON_ORDER_REFUND.toLowerCase();
  const isVoid = actionLower === CON_ORDER_VOID.toLowerCase();
  const isMoneyBack = isRefund || isVoid;
  // Hooks end

  const loadOrderReturnData = useCallback(
    async (silent = false) => {
      if (!ORDERCODE) return;
      if (!silent) setIsLoading(true);
      try {
        const orderResult = await externalgetApi(ORDER_API_04, "get", {
          ORDERCODE,
        });

        const filteredData = Object.values(orderResult["1"])
          .flat()
          .filter((item) => item && item.MENUNAME);
        setOrderData(filteredData);

        const returnData = orderResult["2"] || [];
        const returnHeaders = Array.isArray(orderResult["4"])
          ? orderResult["4"]
          : [];
        setOrderReturnHeaders(returnHeaders);

        const initialQty = {};
        const initialRate = {};
        filteredData.forEach((item) => {
          const alreadyReturnedQty = returnData
            .filter((r) => r.ORDERITEMCODE === item.IDNO)
            .reduce((sum, r) => sum + (parseFloat(r.QTY) || 0), 0);
          const availableQty = Math.max(0, item.QTY - alreadyReturnedQty);
          initialQty[item.IDNO] = availableQty;
          initialRate[item.IDNO] = item.RATE;
        });
        setReturnQty(initialQty);
        setReturnRate(initialRate);

        if (isVoid) {
          const autoChecked = {};
          filteredData.forEach((item) => {
            const alreadyReturnedQty = returnData
              .filter((r) => r.ORDERITEMCODE === item.IDNO)
              .reduce((sum, r) => sum + (parseFloat(r.QTY) || 0), 0);
            if (item.QTY - alreadyReturnedQty > 0) {
              autoChecked[item.IDNO] = true;
            }
          });
          setCheckedRows(autoChecked);
        }

        setAdditionalData(returnData);
        setRemarkOptions(orderResult["3"]);

        const hasReturnableItems = filteredData.some((item) => {
          const alreadyReturned = returnData
            .filter((r) => r.ORDERITEMCODE === item.IDNO)
            .reduce((sum, r) => sum + (parseFloat(r.QTY) || 0), 0);
          return item.QTY - alreadyReturned > 0;
        });
        if (hasReturnableItems) setSubmitted(false);
      } catch (err) {
        console.error("Error fetching order data:", err);
        setError("Failed to load order data");
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [ORDERCODE, isVoid],
  );

  useEffect(() => {
    loadOrderReturnData();
  }, [loadOrderReturnData]);

  const unredeemedCeNotes = useMemo(() => {
    if (!isVoid) return [];
    return getUnredeemedCeNotes(orderReturnHeaders);
  }, [isVoid, orderReturnHeaders]);

  useEffect(() => {
    if (openUnredeemedDialog && unredeemedCeNotes.length === 0) {
      setOpenUnredeemedDialog(false);
    }
  }, [openUnredeemedDialog, unredeemedCeNotes.length]);

  const getCeNoteTypeLabel = (note) =>
    Number(note?.RETURNTYPECODE) === CON_ORTC_EXCHANGE
      ? "Exchange Note"
      : "Credit Note";

  const formatNoteExpiry = (returnDate) => {
    const base = dayjs(returnDate);
    if (!base.isValid()) return "—";
    return base.add(CON_NOTE_EXPIRY_DAYS, "day").format("YYYY-MM-DD");
  };

  const formatNoteDate = (returnDate) => {
    const d = dayjs(returnDate);
    return d.isValid() ? d.format("YYYY-MM-DD") : "—";
  };

  const handleCancelCeNote = async (note) => {
    if (!note?.RETURNNO) return;
    const noteLabel = getCeNoteTypeLabel(note);
    setCancellingReturnNo(note.RETURNNO);
    try {
      await externalpostApi(ORDER_API_19, {
        ORDERCODE: String(ORDERCODE),
        RETURNNO: String(note.RETURNNO),
        STS: CON_STATUS_DELETED,
        ENUSER: USERNAME,
      });
      setSnackbarMessage(`${noteLabel} ${note.RETURNNO} cancelled successfully.`);
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setCancelConfirmNote(null);
      await loadOrderReturnData(true);
    } catch (err) {
      console.error("Failed to cancel note:", err);
      setSnackbarMessage(`Failed to cancel ${noteLabel.toLowerCase()}.`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setCancellingReturnNo(null);
    }
  };

  const getAvailableQty = (item) => {
    const alreadyReturned = (additionalData || [])
      .filter((r) => r.ORDERITEMCODE === item.IDNO)
      .reduce((sum, r) => sum + (parseFloat(r.QTY) || 0), 0);
    return Math.max(0, item.QTY - alreadyReturned);
  };

  const isItemIncluded = (item) =>
    isVoid ? getAvailableQty(item) > 0 : !!checkedRows[item.IDNO];

  const getItemActivityHistory = (idno) =>
    (additionalData || []).filter(
      (r) => String(r.ORDERITEMCODE) === String(idno),
    );

  const toggleHistoryExpand = (idno) => {
    setExpandedHistory((prev) => ({ ...prev, [idno]: !prev[idno] }));
  };

  // Penalty helpers (Refund only) — defined before summary useMemo
  const getPenalty = (idno) =>
    penalty[idno] || { type: "rate", value: "" };

  // Compute effective return rate after penalty deduction
  const getEffectiveRate = (idno, soldRate) => {
    if (!isRefund) return soldRate;
    const p = getPenalty(idno);
    const val = parseFloat(p.value) || 0;
    if (val === 0) return soldRate;
    if (p.type === "rate") {
      const deduction = (val / 100) * soldRate;
      return Math.max(0, soldRate - deduction);
    } else {
      return Math.max(0, soldRate - val);
    }
  };

  // Summary — only selected rows (void: all remaining available items)
  const summary = useMemo(() => {
    const selectedItems = orderData.filter((item) => isItemIncluded(item));
    const totalQty = selectedItems.reduce(
      (sum, item) => sum + (parseFloat(returnQty[item.IDNO]) || 0),
      0,
    );
    const totalAmount = selectedItems.reduce((sum, item) => {
      const qty = parseFloat(returnQty[item.IDNO]) || 0;
      const effectiveRate = getEffectiveRate(item.IDNO, item.RATE);
      return sum + qty * effectiveRate;
    }, 0);
    return { count: selectedItems.length, totalQty, totalAmount };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedRows, returnQty, penalty, orderData, isVoid, additionalData]);

  const orderCannotBeVoided = useMemo(() => {
    if (!isVoid || orderData.length === 0) return false;
    return orderData.every((item) => getAvailableQty(item) === 0);
  }, [isVoid, orderData, additionalData]);

  // Handlers start
  const handleCheckboxChange = (event, idno) => {
    if (isVoid) return;
    setCheckedRows((prev) => ({ ...prev, [idno]: event.target.checked }));
  };

  const handleReturnQtyChange = (idno, value, maxQty) => {
    if (isVoid) return;
    const digitsOnly = String(value).replace(/\D/g, "");
    if (digitsOnly === "") {
      setReturnQty((prev) => ({ ...prev, [idno]: "" }));
      return;
    }
    const parsed = parseInt(digitsOnly, 10);
    if (Number.isNaN(parsed)) return;
    setReturnQty((prev) => ({
      ...prev,
      [idno]: Math.min(Math.max(1, parsed), maxQty),
    }));
  };

  const handleReturnRateChange = (idno, value) => {
    if (value === "") {
      setReturnRate((prev) => ({ ...prev, [idno]: "" }));
      return;
    }
    setReturnRate((prev) => ({ ...prev, [idno]: value }));
  };

  const handleReturnRateBlur = (idno, maxRate) => {
    const val = parseFloat(returnRate[idno]);
    if (isNaN(val) || val <= 0) {
      setReturnRate((prev) => ({ ...prev, [idno]: maxRate }));
    } else {
      setReturnRate((prev) => ({ ...prev, [idno]: Math.min(val, maxRate) }));
    }
  };

  const handleRemarkChange = (event, idno) => {
    setSelectedRemarks((prev) => ({ ...prev, [idno]: event.target.value }));
  };

  const handlePenaltyTypeChange = (idno, type) => {
    setPenalty((prev) => ({
      ...prev,
      [idno]: { ...getPenalty(idno), type, value: "" },
    }));
  };

  const isPenaltyInputAllowed = (type, rawValue, soldRate) => {
    if (rawValue === "") return true;

    const validated = validateCashFields(rawValue);
    if (validated === null) return false;

    // Allow in-progress decimals (e.g. "12." before cents)
    if (validated.endsWith(".")) {
      const head = validated.slice(0, -1);
      if (head === "") return true;
      const headNum = parseFloat(head);
      if (Number.isNaN(headNum)) return false;
      if (type === "rate") return headNum <= PENALTY_RATE_MAX;
      const maxAmount = parseFloat(soldRate);
      return Number.isNaN(maxAmount) || headNum <= maxAmount;
    }

    const num = parseFloat(validated);
    if (Number.isNaN(num)) return false;

    if (type === "rate") return num <= PENALTY_RATE_MAX;

    const maxAmount = parseFloat(soldRate);
    return Number.isNaN(maxAmount) || num <= maxAmount;
  };

  const handlePenaltyValueChange = (idno, value, soldRate) => {
    const p = getPenalty(idno);
    if (!isPenaltyInputAllowed(p.type, value, soldRate)) return;

    const validated = value === "" ? "" : validateCashFields(value);
    if (value !== "" && validated === null) return;

    setPenalty((prev) => ({
      ...prev,
      [idno]: { ...p, value: validated ?? "" },
    }));
  };

  const handlePenaltyValueBlur = (idno, soldRate) => {
    const p = getPenalty(idno);
    const val = parseFloat(p.value);
    if (p.value === "" || p.value === "." || Number.isNaN(val) || val < 0) {
      setPenalty((prev) => ({
        ...prev,
        [idno]: { ...p, value: "" },
      }));
      return;
    }

    if (p.type === "rate" && val > PENALTY_RATE_MAX) {
      setPenalty((prev) => ({
        ...prev,
        [idno]: { ...p, value: "" },
      }));
      return;
    }

    const maxAmount = parseFloat(soldRate);
    if (p.type === "amount" && !Number.isNaN(maxAmount) && val > maxAmount) {
      setPenalty((prev) => ({
        ...prev,
        [idno]: { ...p, value: "" },
      }));
    }
  };

  // Compute effective return rate after penalty deduction — moved above summary useMemo
  const handleOpenDialog = () => {
    if (orderCannotBeVoided) {
      setSnackbarMessage("This order cannot be voided.");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }
    if (summary.count === 0) {
      setSnackbarMessage(
        `Please select at least one item to ${actionText?.toLowerCase() || "return"}.`,
      );
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleConfirmReturnOrder = async () => {
    setOpenDialog(false);
    await handleReturnOrder();
  };

  const handleReturnOrder = async () => {
    setSubmitted(true);
    const returnOrderData = orderData.filter((item) => isItemIncluded(item));

    // Validate: reason is mandatory per selected item (not required for void)
    if (!isVoid) {
      const missingReason = returnOrderData.find(
        (item) => !selectedRemarks[item.IDNO] || Number(selectedRemarks[item.IDNO]) <= 0
      );
      if (missingReason) {
        setSnackbarMessage("Please select a return reason for each selected item.");
        setSnackbarSeverity("warning");
        setOpenSnackbar(true);
        setSubmitted(false);
        return;
      }
    }

    const RETURNNO = generateReturnNo(getReturnType(), USERREFCODE, RESTCODE);
    const payload = {
      returnorderdt: returnOrderData.map((item) => {
        const qty = parseFloat(returnQty[item.IDNO]) || item.QTY;
        const effectiveRate = getEffectiveRate(item.IDNO, item.RATE);
        const penaltyAmt = item.RATE - effectiveRate;
        return {
          idno: item.IDNO,
          seqid: item.SEQID,
          ordercode: ORDERCODE,
          menucatcode: item.MENUCATCODE,
          menucode: item.MENUCODE,
          odremarkcode: Number(selectedRemarks[item.IDNO]) || 0,
          qty,
          penaltyamount: parseFloat(penaltyAmt.toFixed(2)),
          rate: parseFloat(effectiveRate.toFixed(2)),
          amount: parseFloat((qty * effectiveRate).toFixed(2)),
          isrefund: isMoneyBack ? CON_STATUS_YES : CON_STATUS_NO,
          sts: CON_STATUS_RETURN,
        };
      }),
      ordercode: String(ORDERCODE),
      // Backend expects these exact field names (C# model validation)
      RETURNNO,
      RETURNTYPECODE: getReturnType(),
      remark: notes || "",
      returndate: new Date().toISOString(),
      isrefund: isMoneyBack ? CON_STATUS_YES : CON_STATUS_NO,
      refundamount: summary.totalAmount,
      sts: isMoneyBack ? CON_STATUS_RETURN : CON_STATUS_PENDING,
      refundsts: isMoneyBack ? CON_STATUS_ACTIVE : CON_STATUS_PENDING,
      enuser: USERNAME,
    };

    try {
      await externalpostApi(ORDER_API_04, payload);
      setSnackbarMessage(`${actionText} submitted successfully!`);
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      // For Return / Exchange — fetch note and show modal
      const isNoteAction =
        actionText?.toLowerCase() === CON_ORDER_RETURN.toLowerCase() ||
        actionText?.toLowerCase() === CON_ORDER_EXCHANGE.toLowerCase();

      if (isNoteAction) {
        try {
          const noteResult = await externalgetApi(ORDER_API_04, "get", {
            ORDERCODE,
          });
          const updatedReturnData = noteResult["2"] || [];
          // Get the latest return entries (those just submitted)
          const submittedIDNOs = returnOrderData.map((i) => i.IDNO);
          const latestReturns = updatedReturnData.filter((r) =>
            submittedIDNOs.includes(r.ORDERITEMCODE)
          );
          const noteDate = new Date();
          const expiry = new Date(noteDate);
          expiry.setDate(expiry.getDate() + 30);
          const fmtYmd = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
              d.getDate()
            ).padStart(2, "0")}`;

          setNoteData({
            items: returnOrderData.map((item) => {
              const qty = parseFloat(returnQty[item.IDNO]) || item.QTY;
              const effectiveRate = getEffectiveRate(item.IDNO, item.RATE);
              return {
                MENUNAME: item.MENUNAME,
                QTY: qty,
                RATE: effectiveRate,
                AMOUNT: qty * effectiveRate,
                REMARK:
                  remarkOptions.find(
                    (r) => r.ODREMARKCODE === selectedRemarks[item.IDNO]
                  )?.REMARK || "",
              };
            }),
            totalAmount: summary.totalAmount,
            totalQty: summary.totalQty,
            notes,
            ordercode: ORDERCODE,
            invno: INVNO,
            returnno: RETURNNO,
            date: noteDate.toLocaleString(),
            expiryDate: fmtYmd(expiry),
          });
          setOpenNoteModal(true);
        } catch (noteErr) {
          console.error("Error fetching note data:", noteErr);
        }
      }
    } catch (err) {
      console.error("Error returning order:", err);
      setSnackbarMessage("Failed to submit order return.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      // Re-enable button on failure
      setSubmitted(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setError(null);
  };

  const gotToView = () => navigate("/orderlist");

  const getReturnType = () => {
    const type = actionText?.toLowerCase();
    switch (type) {
      case CON_ORDER_RETURN.toLowerCase():
        return CON_ORTC_RETURN;
      case CON_ORDER_REFUND.toLowerCase():
        return CON_ORTC_REFUND;
      case CON_ORDER_VOID.toLowerCase():
        return CON_ORTC_VOID;
      case CON_ORDER_EXCHANGE.toLowerCase():
        return CON_ORTC_EXCHANGE;
      default:
        return CON_ORTC_RETURN;
    }
  };

  // Handlers end

  const buildNotePrintHtml = ({ storeName, address, actionText, noteData }) => {
    const esc = (v) =>
      String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const items = Array.isArray(noteData?.items) ? noteData.items : [];
    const rows = items
      .map((it) => {
        const main = `
          <tr>
            <td class="item">${esc(it.MENUNAME)}</td>
            <td class="qty">${esc(it.QTY)}</td>
            <td class="amt">${esc(currencyFormat(it.AMOUNT))}</td>
          </tr>`;
        const remark = it.REMARK
          ? `
          <tr>
            <td class="remark" colspan="3">↳ ${esc(it.REMARK)}</td>
          </tr>`
          : "";
        return main + remark;
      })
      .join("");

    const titleText =
      actionText === CON_ORDER_EXCHANGE ? "EXCHANGE NOTE" : "CREDIT NOTE";

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(titleText)}</title>
  <style>
    *{box-sizing:border-box;}
    body{margin:0;padding:12px;font-family:Arial, sans-serif;color:#000;}
    .note{max-width:360px;margin:0 auto;}
    .center{text-align:center;}
    .shop{font-weight:700;font-size:16px;line-height:1.2;}
    .addr{font-size:12px;color:#444;line-height:1.2;margin-top:2px;}
    .title{font-weight:800;font-size:14px;letter-spacing:.06em;margin:10px 0;padding:8px 0;border-top:1px dashed #000;border-bottom:1px dashed #000;}
    .meta{font-size:12px;margin:8px 0;}
    .metaRow{display:flex;justify-content:space-between;gap:12px;margin:3px 0;}
    .metaLabel{color:#444;}
    .metaVal{font-weight:700;}
    table{width:100%;border-collapse:collapse;font-size:12px;}
    thead th{border-top:1px dashed #000;border-bottom:1px dashed #000;padding:6px 0;font-weight:800;}
    td{padding:4px 0;vertical-align:top;}
    td.item{width:65%;}
    td.qty{width:10%;text-align:center;}
    td.amt{width:25%;text-align:right;}
    td.remark{padding-left:10px;color:#555;font-size:11px;}
    .totals{border-top:1px dashed #000;margin-top:10px;padding-top:8px;}
    .totalRow{display:flex;justify-content:space-between;font-size:12px;margin:3px 0;}
    .grand{font-weight:900;font-size:14px;}
    @media print{ body{padding:0;} .note{max-width:100%;} }
  </style>
</head>
<body>
  <div class="note">
    <div class="center">
      <div class="shop">${esc(storeName)}</div>
      ${address ? `<div class="addr">${esc(address)}</div>` : ""}
      <div class="title">${esc(titleText)}</div>
    </div>

    <div class="meta">
      <div class="metaRow"><div class="metaLabel">Order #</div><div class="metaVal">${esc(noteData?.invno || noteData?.ordercode)}</div></div>
      <div class="metaRow"><div class="metaLabel">CN/EX No</div><div class="metaVal">${esc(noteData?.returnno || "—")}</div></div>
      <div class="metaRow"><div class="metaLabel">Date</div><div>${esc(noteData?.date || "")}</div></div>
      <div class="metaRow"><div class="metaLabel">Expiry Date</div><div class="metaVal">${esc(noteData?.expiryDate || "—")}</div></div>
      <div class="metaRow"><div class="metaLabel">Type</div><div class="metaVal">${esc(actionText || "Return")}</div></div>
    </div>

    <table>
      <thead>
        <tr>
          <th style="text-align:left">Item</th>
          <th style="text-align:center">Qty</th>
          <th style="text-align:right">Amount</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>

    <div class="totals">
      <div class="totalRow"><div class="metaLabel">Total Qty</div><div class="metaVal">${esc(noteData?.totalQty || 0)}</div></div>
      <div class="totalRow grand"><div>Total Amount</div><div>${esc(currencyFormat(noteData?.totalAmount || 0))}</div></div>
    </div>
  </div>
</body>
</html>`;
  };

  const handlePrintNote = () => {
    if (!noteData) return;
    const html = buildNotePrintHtml({
      storeName: RESTNAME || COMPNAME || "",
      address: ADDRESS || "",
      actionText,
      noteData,
    });

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 500);
    };
  };
  // Handlers end

  if (!ORDERCODE) {
    return (
      <div>
        <Navbar />
        <Box sx={{ p: 4, mt: { xs: "170px", md: "155px" } }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            No order selected. Please go back and select an order from the order list.
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/orderlist")}
            sx={{
              background: theme.palette.primary.main,
              color: theme.palette.text.buttontxt,
              textTransform: "capitalize",
            }}
          >
            Back to Order List
          </Button>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      {/* AppBar / Breadcrumb */}
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs2}
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none">Home</Link>
                <Link underline="none">Returns</Link>
                <Link underline="none">{actionText || "Order Return"}</Link>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                sx={{
                  ...styles.AI_fixedStack,
                  direction: "row",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  onClick={gotToView}
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    background: theme.palette.primary.main,
                    color: theme.palette.text.buttontxt,
                    textTransform: "capitalize",
                    width: "80px",
                  }}
                >
                  Back
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.AI_AddStyledBox}>
        {/* Page title toolbar */}
        <Toolbar
          sx={{
            maxWidth: "100%",
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            mt: { xs: "170px", md: "155px" },
            background: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.stroke.main}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: "1 1 100%",
            }}
          >
            {/* <ReceiptLongIcon sx={{ color: theme.palette.primary.main }} /> */}
            <Typography
              variant="h6"
              component="div"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
              }}
            >
              Order {actionText || "Return"}&nbsp;
              <Chip
                label={`# ${INVNO || ORDERCODE}`}
                size="small"
                sx={{
                  backgroundColor: `${theme.palette.primary.main}18`,
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: "0.8rem",
                }}
              />
            </Typography>
          </Box>
          {/* <Button
            variant="contained"
            startIcon={<UndoIcon />}
            onClick={handleOpenDialog}
            sx={{
              background: theme.palette.primary.main,
              color: theme.palette.text.buttontxt,
              textTransform: "capitalize",
              whiteSpace: "nowrap",
              px: 3,
            }}
          >
            {actionText || "Return Order"}
          </Button> */}
        </Toolbar>

        {/* Helper hint */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: `${theme.palette.system?.info || "#0CA4FF"}12`,
            borderBottom: `1px solid ${theme.palette.stroke.main}`,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: 16,
              color: theme.palette.system?.info || "#0CA4FF",
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {isVoid
              ? "All remaining items are selected for void."
              : `Please select the items for your request. Selecting an item enables editing for the ${actionLabel} Quantity.`}
          </Typography>
        </Box>

        {isVoid && orderCannotBeVoided && !isLoading && (
          <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
            This order cannot be voided. All items have already been returned,
            refunded, exchanged or this has been voided.
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            {/* ── Item cards ── */}
            {orderData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                  gap: 1,
                  color: theme.palette.text.secondary,
                }}
              >
                <ShoppingBagOutlinedIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2">
                  No items found for this order.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {orderData.map((item, index) => {
                  const availableQty = getAvailableQty(item);
                  const isSelected = isItemIncluded(item);
                  const isReturned =
                    item.QTY > availableQty
                      ? true
                      : false;
                  const isFullyReturned = availableQty === 0;
                  const disabled = isFullyReturned;
                  const qty = parseFloat(returnQty[item.IDNO]) || 0;
                  const rate = parseFloat(returnRate[item.IDNO]) || 0;
                  const rowAmount = qty * rate;
                  const itemHistory = getItemActivityHistory(item.IDNO);

                  return (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        border: `1.5px solid ${
                          isSelected
                            ? theme.palette.primary.main
                            : theme.palette.stroke.main
                        }`,
                        borderRadius: 2,
                        overflow: "hidden",
                        opacity: disabled ? 0.55 : 1,
                        transition: "border-color 0.2s, box-shadow 0.2s",
                        boxShadow: isSelected
                          ? `0 0 0 3px ${theme.palette.primary.main}20`
                          : "none",
                        backgroundColor: disabled
                          ? theme.palette.background.tertiary
                          : isSelected
                            ? `${theme.palette.primary.main}06`
                            : theme.palette.background.paper,
                      }}
                    >
                      {/* ── Responsive horizontal row ── */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: { xs: 1, md: 2 },
                          py: 1.25,
                          flexWrap: { xs: "wrap", md: "nowrap" },
                        }}
                      >
                        {/* ── ROW 1 on mobile: Checkbox + Item name ── */}

                        {/* Checkbox */}
                        <Box sx={{ flex: "0 0 auto", order: 0 }}>
                          <Tooltip
                            title={
                              isFullyReturned
                                ? "Fully returned — no available qty"
                                : isVoid
                                  ? "All remaining items are included in this void"
                                  : ""
                            }
                            disableHoverListener={!isFullyReturned && !isVoid}
                          >
                            <span>
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) =>
                                  handleCheckboxChange(e, item.IDNO)
                                }
                                disabled={disabled || (isVoid && availableQty > 0)}
                                color="primary"
                                size="small"
                              />
                            </span>
                          </Tooltip>
                        </Box>

                        {/* Item name — takes remaining width on mobile, fixed on desktop */}
                        <Box
                          sx={{
                            flex: { xs: "1 1 0", md: "0 0 180px" },
                            minWidth: 0,
                            order: 1,
                            cursor: disabled || isVoid ? "default" : "pointer",
                          }}
                          onClick={() => {
                            if (!disabled && !isVoid)
                              setCheckedRows((prev) => ({
                                ...prev,
                                [item.IDNO]: !prev[item.IDNO],
                              }));
                          }}
                        >
                          <Tooltip
                            title={item.MENUNAME || ""}
                            arrow
                            placement="top-start"
                            enterDelay={500}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: isSelected
                                  ? theme.palette.primary.main
                                  : theme.palette.text.primary,
                              }}
                            >
                              {item.MENUNAME}
                            </Typography>
                          </Tooltip>
                          {isFullyReturned && (
                            <Chip
                              label="Fully Returned"
                              size="small"
                              sx={{
                                mt: 0.25,
                                fontSize: "0.6rem",
                                height: 16,
                                backgroundColor: `${theme.palette.system?.danger || "#FF3030"}18`,
                                color:
                                  theme.palette.system?.danger || "#FF3030",
                              }}
                            />
                          )}
                          {!isFullyReturned && isReturned && (
                            <Chip
                              label="Partially Returned"
                              size="small"
                              sx={{
                                mt: 0.25,
                                fontSize: "0.6rem",
                                height: 16,
                                backgroundColor: `${theme.palette.system?.warning || "#FF8C00"}18`,
                                color:
                                  theme.palette.system?.warning || "#FF8C00",
                              }}
                            />
                          )}
                        </Box>

                        {/* ── ROW 2 on mobile: read-only reference fields ── */}

                        {/* Available Qty */}
                        <TextField
                          label="Available Qty"
                          size="small"
                          value={availableQty}
                          InputProps={{ readOnly: true }}
                          inputProps={{ style: { textAlign: "center" } }}
                          sx={{
                            width: { xs: "calc(50% - 4px)", md: 100 },
                            flex: { xs: "0 0 calc(50% - 4px)", md: "0 0 auto" },
                            order: { xs: 2, md: 2 },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor:
                                availableQty === 0
                                  ? `${theme.palette.system?.danger || "#FF3030"}10`
                                  : availableQty < item.QTY
                                    ? `${theme.palette.system?.warning || "#FF8C00"}10`
                                    : theme.palette.background.default,
                            },
                            "& .MuiInputBase-input": {
                              color:
                                availableQty === 0
                                  ? theme.palette.system?.danger || "#FF3030"
                                  : availableQty < item.QTY
                                    ? theme.palette.system?.warning || "#FF8C00"
                                    : theme.palette.text.primary,
                              fontWeight: 700,
                            },
                          }}
                        />

                        {/* Sold Rate */}
                        <TextField
                          label="Sold Rate"
                          size="small"
                          value={currencyFormat(item.RATE)}
                          InputProps={{ readOnly: true }}
                          inputProps={{ style: { textAlign: "right" } }}
                          sx={{
                            width: { xs: "calc(50% - 4px)", md: 110 },
                            flex: { xs: "0 0 calc(50% - 4px)", md: "0 0 auto" },
                            order: { xs: 3, md: 3 },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: theme.palette.background.default,
                            },
                          }}
                        />

                        {/* Divider — desktop only */}
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            display: { xs: "none", md: "block" },
                            order: 4,
                          }}
                        />

                        {/* ── ROW 3 on mobile: editable fields ── */}

                        {/* Return / Void Qty */}
                        <TextField
                          label={`${actionLabel} Qty`}
                          type="text"
                          size="small"
                          value={returnQty[item.IDNO] ?? availableQty}
                          onChange={(e) =>
                            handleReturnQtyChange(
                              item.IDNO,
                              e.target.value,
                              availableQty,
                            )
                          }
                          onBlur={() => {
                            if (
                              !isVoid &&
                              (returnQty[item.IDNO] === "" ||
                                returnQty[item.IDNO] === undefined)
                            ) {
                              setReturnQty((prev) => ({
                                ...prev,
                                [item.IDNO]: 1,
                              }));
                            }
                          }}
                          disabled={!isSelected || disabled}
                          InputProps={
                            isVoid ? { readOnly: true } : undefined
                          }
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            style: { textAlign: "center" },
                          }}
                          sx={{
                            width: { xs: "calc(33% - 4px)", md: 100 },
                            flex: { xs: "0 0 calc(33% - 4px)", md: "0 0 auto" },
                            order: { xs: 5, md: 5 },
                            ...(isVoid && {
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: theme.palette.background.default,
                              },
                            }),
                            ...noSpinnerSx,
                          }}
                        />

                        {/* Penalty (Refund only) — type dropdown + value input */}
                        {isRefund && (
                          <>
                            <FormControl
                              size="small"
                              sx={{
                                width: { xs: "calc(33% - 4px)", md: 110 },
                                flex: { xs: "0 0 calc(33% - 4px)", md: "0 0 auto" },
                                order: { xs: 6, md: 6 },
                              }}
                            >
                              <InputLabel>Penalty</InputLabel>
                              <Select
                                label="Penalty"
                                value={getPenalty(item.IDNO).type}
                                onChange={(e) =>
                                  handlePenaltyTypeChange(item.IDNO, e.target.value)
                                }
                                disabled={!isSelected || disabled}
                              >
                                <MenuItem value="rate">Rate (%)</MenuItem>
                                <MenuItem value="amount">Amount</MenuItem>
                              </Select>
                            </FormControl>

                            <TextField
                              label={
                                getPenalty(item.IDNO).type === "rate"
                                  ? "Penalty %"
                                  : "Penalty Amt"
                              }
                              type="text"
                              size="small"
                              value={getPenalty(item.IDNO).value}
                              onChange={(e) =>
                                handlePenaltyValueChange(
                                  item.IDNO,
                                  e.target.value,
                                  item.RATE,
                                )
                              }
                              onBlur={() =>
                                handlePenaltyValueBlur(item.IDNO, item.RATE)
                              }
                              disabled={!isSelected || disabled}
                              inputProps={{
                                inputMode: "decimal",
                                style: { textAlign: "right" },
                              }}
                              sx={{
                                width: { xs: "calc(33% - 4px)", md: 110 },
                                flex: { xs: "0 0 calc(33% - 4px)", md: "0 0 auto" },
                                order: { xs: 7, md: 7 },
                                ...noSpinnerSx,
                              }}
                            />
                          </>
                        )}

                        {/* Amount — read-only, reflects effective rate */}
                        <TextField
                          label="Amount"
                          size="small"
                          value={
                            isSelected
                              ? currencyFormat(
                                  (parseFloat(returnQty[item.IDNO]) || 0) *
                                    getEffectiveRate(item.IDNO, item.RATE),
                                )
                              : "—"
                          }
                          InputProps={{ readOnly: true }}
                          inputProps={{
                            style: { textAlign: "right", fontWeight: 700 },
                          }}
                          sx={{
                            width: { xs: isRefund ? "100%" : "calc(33% - 4px)", md: 110 },
                            flex: { xs: isRefund ? "1 1 100%" : "0 0 calc(33% - 4px)", md: "0 0 auto" },
                            order: { xs: 8, md: 8 },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: isSelected
                                ? `${theme.palette.primary.main}10`
                                : theme.palette.background.default,
                            },
                            "& .MuiInputBase-input": {
                              color: isSelected
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                              fontWeight: isSelected ? 700 : 400,
                            },
                          }}
                        />

                        {/* Reason — not shown for void */}
                        {!isVoid && (
                        <FormControl
                          size="small"
                          sx={{
                            flex: { xs: "1 1 100%", md: "1 1 160px" },
                            minWidth: { xs: "100%", md: 140 },
                            order: { xs: 9, md: 9 },
                          }}
                        >
                          <InputLabel>
    Reason <span style={{ color: "red" }}>*</span>
  </InputLabel>

                          <Select
                            label="Reason"
                            required
                            value={
                              selectedRemarks[item.IDNO] || ""
                            }
                            onChange={(e) => handleRemarkChange(e, item.IDNO)}
                            disabled={!isSelected || disabled}
                          >
                            {remarkOptions.map((remark) => (
                              <MenuItem
                                key={remark.ODREMARKCODE}
                                value={remark.ODREMARKCODE}
                              >
                                {remark.REMARK}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        )}
                      </Box>
                      {itemHistory.length > 0 && (
                        <ItemActivityHistory
                          history={itemHistory}
                          expanded={!!expandedHistory[item.IDNO]}
                          onToggle={() => toggleHistoryExpand(item.IDNO)}
                          theme={theme}
                        />
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}

            {/* ── Summary + Notes ── */}
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: `2px solid ${theme.palette.stroke.main}`,
              }}
            >
              <Grid container spacing={2}>
                {/* Notes */}
                <Grid item xs={12} md={7}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      mb: 1,
                    }}
                  >
                    Notes / Remarks
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    placeholder={`Add any notes about this ${actionLower || "return"}...`}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    size="small"
                    inputProps={{ maxLength: 200 }}
                    helperText={`${notes.length}/200`}
                  />
                </Grid>

                {/* Summary card */}
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      background: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.input_field.stroke}`,
                      borderRadius: 2,
                      p: 2,
                      boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.secondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        display: "block",
                        mb: 1.5,
                      }}
                    >
                      {actionLabel} Summary
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Items Selected
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {summary.count}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Total {actionLabel} Qty
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {summary.totalQty}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        Total {actionLabel} Amount
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {currencyFormat(summary.totalAmount)}
                      </Typography>
                    </Box>

                    {isVoid && unredeemedCeNotes.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          This order has {unredeemedCeNotes.length} unredeemed
                          credit/exchange note
                          {unredeemedCeNotes.length > 1 ? "s" : ""}. Consider
                          cancelling them before voiding.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => setOpenUnredeemedDialog(true)}
                          sx={{ textTransform: "capitalize" }}
                        >
                          View Unredeemed Credit/Exchange Notes
                        </Button>
                      </Alert>
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<UndoIcon />}
                      onClick={handleOpenDialog}
                      disabled={summary.count === 0 || submitted || orderCannotBeVoided}
                      sx={{
                        mt: 2,
                        background: theme.palette.primary.main,
                        color: theme.palette.text.buttontxt,
                        textTransform: "capitalize",
                        "&:hover": {
                          background: theme.palette.primary.main,
                          opacity: 0.9,
                        },
                        "&.Mui-disabled": { opacity: 0.5 },
                      }}
                    >
                      {submitted ? "Submitted" : (actionText || "Return Order")}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="confirmation-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="confirmation-dialog-title" sx={{ pb: 1 }}>
          Confirm {actionLabel} Order
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            You are about to {actionLower || "return"}{" "}
            <strong>{summary.count}</strong> item(s) with a total of{" "}
            <strong style={{ color: theme.palette.primary.main }}>
              {currencyFormat(summary.totalAmount)}
            </strong>
            . This action cannot be undone.
          </Typography>
          {isVoid && unredeemedCeNotes.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This order still has {unredeemedCeNotes.length} unredeemed
              credit/exchange note{unredeemedCeNotes.length > 1 ? "s" : ""}. You
              may cancel them from the list before or after voiding.
            </Alert>
          )}
         {notes.trim() && (
  <TextField
    label="Notes"
    value={notes}
    multiline
    fullWidth
    size="small"
    variant="outlined"
    InputProps={{
      readOnly: true,
    }}
    sx={{
      mt: 1,
    }}
  />
)}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ textTransform: "capitalize", borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReturnOrder}
            variant="contained"
            sx={{
              background: theme.palette.primary.main,
              color: theme.palette.text.buttontxt,
              textTransform: "capitalize",
              borderRadius: 2,
              px: 3,
              "&:hover": {
                background: theme.palette.primary.main,
                opacity: 0.9,
              },
            }}
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unredeemed credit/exchange notes dialog (void only) */}
      <Dialog
        open={openUnredeemedDialog}
        onClose={() => setOpenUnredeemedDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="span" fontWeight={700}>
            Unredeemed Credit/Exchange Notes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Cancel pending notes before voiding this order. Claimed or expired
            notes are not shown.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {unredeemedCeNotes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No unredeemed credit or exchange notes for this order.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ overflowX: "auto" }}
            >
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>
                      Note No
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                    >
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      Return Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      Expiry
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      Status
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, whiteSpace: "nowrap", width: 100 }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unredeemedCeNotes.map((note) => {
                    const status = getNoteRefundStatus(note);
                    const noteTypeLabel = getCeNoteTypeLabel(note);
                    const isExchange =
                      Number(note.RETURNTYPECODE) === CON_ORTC_EXCHANGE;
                    return (
                      <TableRow
                        key={note.RETURNNO || note.ODRETURNCODE}
                        hover
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title={noteTypeLabel}>
                            <Chip
                              label={isExchange ? "Exchange" : "Credit"}
                              color={isExchange ? "warning" : "primary"}
                              size="small"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ wordBreak: "break-word" }}
                          >
                            {note.RETURNNO || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {currencyFormat(note.REFUNDAMOUNT)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatNoteDate(note.RETURNDATE)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatNoteExpiry(note.RETURNDATE)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            label={status.label}
                            color={status.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={cancellingReturnNo === note.RETURNNO}
                            onClick={() => setCancelConfirmNote(note)}
                            sx={{ textTransform: "capitalize", minWidth: 72 }}
                          >
                            {cancellingReturnNo === note.RETURNNO ? (
                              <CircularProgress size={18} />
                            ) : (
                              "Cancel"
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setOpenUnredeemedDialog(false)}
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel credit/exchange note confirmation */}
      <Dialog
        open={!!cancelConfirmNote}
        onClose={() => !cancellingReturnNo && setCancelConfirmNote(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Cancel {getCeNoteTypeLabel(cancelConfirmNote || {})}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Cancel {getCeNoteTypeLabel(cancelConfirmNote || {}).toLowerCase()}{" "}
            <strong>{cancelConfirmNote?.RETURNNO}</strong>? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCancelConfirmNote(null)}
            disabled={!!cancellingReturnNo}
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          >
            No
          </Button>
          <Button
            onClick={() => handleCancelCeNote(cancelConfirmNote)}
            disabled={!!cancellingReturnNo}
            color="error"
            variant="contained"
            sx={{ textTransform: "capitalize" }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ── Credit / Exchange Note Modal ── */}
      <Dialog
        open={openNoteModal}
        onClose={() => setOpenNoteModal(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 320,
            maxWidth: 320,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {actionText === CON_ORDER_EXCHANGE ? "Exchange Note" : "Credit Note"}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 2, px: 2 }}>
          {/* Printable area */}
          <Box
            ref={printRef}
            sx={{
              fontFamily: "'Courier New', monospace",
              fontSize: "11px",
              width: "100%",
              color: "#000",
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Typography
                sx={{
                  fontFamily: "inherit",
                  fontSize: "13px",
                  fontWeight: 700,
                  lineHeight: 1.3,
                }}
              >
                {RESTNAME || COMPNAME || ""}
              </Typography>
              {ADDRESS && (
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "10px",
                    color: "#444",
                    lineHeight: 1.3,
                  }}
                >
                  {ADDRESS}
                </Typography>
              )}
              <Box
                sx={{
                  borderTop: "1px dashed #000",
                  borderBottom: "1px dashed #000",
                  my: 0.75,
                  py: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                  }}
                >
                  {actionText === CON_ORDER_EXCHANGE
                    ? "*** EXCHANGE NOTE ***"
                    : "*** CREDIT NOTE ***"}
                </Typography>
              </Box>
            </Box>

            {/* Meta */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontFamily: "inherit", fontSize: "10px" }}>
                  Order #
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "10px",
                    fontWeight: 700,
                  }}
                >
                  {noteData?.invno || noteData?.ordercode}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontFamily: "inherit", fontSize: "10px" }}>
                  CN/EX No
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "10px",
                    fontWeight: 700,
                  }}
                >
                  {noteData?.returnno || "—"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontFamily: "inherit", fontSize: "10px" }}>
                  Date
                </Typography>
                <Typography sx={{ fontFamily: "inherit", fontSize: "10px" }}>
                  {noteData?.date}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontFamily: "inherit", fontSize: "10px" }}>
                  Expiry Date
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "10px",
                    fontWeight: 700,
                  }}
                >
                  {noteData?.expiryDate || "—"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontFamily: "inherit", fontSize: "10px" }}>
                  Type
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "10px",
                    fontWeight: 700,
                  }}
                >
                  {actionText}
                </Typography>
              </Box>
            </Box>

            {/* Column headers */}
            <Box
              sx={{
                borderTop: "1px dashed #000",
                borderBottom: "1px dashed #000",
                py: 0.4,
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "inherit",
                  fontSize: "10px",
                  fontWeight: 700,
                  flex: 1,
                }}
              >
                Item
              </Typography>
              <Typography
                sx={{
                  fontFamily: "inherit",
                  fontSize: "10px",
                  fontWeight: 700,
                  width: 28,
                  textAlign: "center",
                }}
              >
                Qty
              </Typography>
              <Typography
                sx={{
                  fontFamily: "inherit",
                  fontSize: "10px",
                  fontWeight: 700,
                  width: 52,
                  textAlign: "right",
                }}
              >
                Rate
              </Typography>
              <Typography
                sx={{
                  fontFamily: "inherit",
                  fontSize: "10px",
                  fontWeight: 700,
                  width: 56,
                  textAlign: "right",
                }}
              >
                Amount
              </Typography>
            </Box>

            {/* Items */}
            {(noteData?.items || []).map((item, i) => (
              <Box key={i} sx={{ mb: 0.25 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "10px",
                      flex: 1,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      maxWidth: 120,
                    }}
                  >
                    {item.MENUNAME}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "10px",
                      width: 28,
                      textAlign: "center",
                    }}
                  >
                    {item.QTY}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "10px",
                      width: 52,
                      textAlign: "right",
                    }}
                  >
                    {currencyFormat(item.RATE)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "10px",
                      width: 56,
                      textAlign: "right",
                    }}
                  >
                    {currencyFormat(item.AMOUNT)}
                  </Typography>
                </Box>
                {item.REMARK && (
                  <Typography
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "9px",
                      color: "#555",
                      pl: 1,
                    }}
                  >
                    ↳ {item.REMARK}
                  </Typography>
                )}
              </Box>
            ))}

            {/* Totals */}
            <Box
              sx={{
                borderTop: "1px dashed #000",
                mt: 0.75,
                pt: 0.5,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "10px",
                    color: "#444",
                  }}
                >
                  Total Qty
                </Typography>
                <Typography
                  sx={{ fontFamily: "inherit", fontSize: "10px", fontWeight: 700 }}
                >
                  {noteData?.totalQty}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  Total Amount
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  {currencyFormat(noteData?.totalAmount || 0)}
                </Typography>
              </Box>
            </Box>

            {/* Notes */}
          {noteData?.notes && (
  <TextField
    label="Note"
    value={noteData.notes}
    multiline
    fullWidth
    size="small"
    variant="outlined"
    InputProps={{
      readOnly: true,
    }}
    sx={{
      mt: 0.75,
      "& .MuiInputBase-input": {
        fontSize: "9px",
        fontFamily: "inherit",
      },
      "& .MuiInputLabel-root": {
        fontSize: "9px",
      },
    }}
  />
)}

            {/* Footer */}
            <Box
              sx={{
                borderTop: "1px dashed #000",
                mt: 1,
                pt: 0.5,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{ fontFamily: "inherit", fontSize: "9px", color: "#555" }}
              >
                {actionText === CON_ORDER_EXCHANGE
                  ? "Present this note to exchange your item(s)."
                  : "Present this note to claim your refund."}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "inherit",
                  fontSize: "9px",
                  color: "#555",
                  mt: 0.25,
                }}
              >
                Thank you for your patience.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrintNote}
            sx={{
              background: theme.palette.primary.main,
              color: theme.palette.text.buttontxt,
              textTransform: "capitalize",
            }}
          >
            Print Note
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default OrderReturn;
